import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api, { documentApi } from '../api';
import { PageHeader, Card, Button, LoadingSpinner, StatsCard, Pagination } from '../components/UI';
import * as I from 'lucide-react';

const TdsPaidBatchesPage = ({ navigate, setSelectedBatch }) => {
  const [currentPage, setCurrentPage] = useState(0); // 0-based
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);

  const [tdsBatches, setTdsBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(true);

  // filter state
  const [yearFilter, setYearFilter] = useState('ALL');
  const [quarterFilter, setQuarterFilter] = useState('ALL');
  const [dateFilter, setDateFilter] = useState(''); // yyyy-MM-dd

  // View challan state
  const [viewChallanOpen, setViewChallanOpen] = useState(false);
  const [selectedBatchForChallan, setSelectedBatchForChallan] = useState(null);
  const [challanDetails, setChallanDetails] = useState(null);
  const [loadingChallan, setLoadingChallan] = useState(false);
  const [challanError, setChallanError] = useState('');

  // invoices-in-batch state
  const [viewInvoicesOpen, setViewInvoicesOpen] = useState(false);
  const [selectedBatchForInvoices, setSelectedBatchForInvoices] = useState(null);
  const [batchInvoices, setBatchInvoices] = useState([]);
  const [loadingBatchInvoices, setLoadingBatchInvoices] = useState(false);
  const [batchInvoicesError, setBatchInvoicesError] = useState('');

  // vendor cache
  const [vendorMap, setVendorMap] = useState({});

  // pdf viewer
  const [showPdf, setShowPdf] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  const handleViewChallanDocument = async () => {
    if (!selectedBatchForChallan?.id) {
      toast.warning('No batch selected for viewing challan.');
      return;
    }

    try {
      console.log('[TDS-PAID-BATCHES] Fetching challan document URL for batch:', selectedBatchForChallan.id);

      // Get presigned URL from backend using the new S3 endpoint
      const response = await documentApi.getTdsChallanDocumentUrl(selectedBatchForChallan.id);
      const documentUrl = response.data; // Backend returns string directly
      console.log('[TDS-PAID-BATCHES] Received presigned URL:', documentUrl);

      if (!documentUrl) {
        toast.warning('No challan document available for this batch.');
        return;
      }

      setPdfUrl(documentUrl);
      setShowPdf(true);
      console.log('[TDS-PAID-BATCHES] PDF URL set and modal shown');
    } catch (error) {
      console.error('[TDS-PAID-BATCHES] Error fetching challan document URL:', error);
      console.error('[TDS-PAID-BATCHES] Error status:', error.status);
      console.error('[TDS-PAID-BATCHES] Error response status:', error.response?.status);

      if (error.status === 404 || error.response?.status === 404) {
        toast.warning('No challan document is available for this batch.');
      } else {
        toast.error('Failed to fetch challan document. Please try again or contact support.');
      }
    }
  };

  const fetchTdsBatches = useCallback(async () => {
    setLoadingBatches(true);
    try {
      const resp = await api.get('/tds/batches/all');
      const data = Array.isArray(resp.data) ? resp.data : [];
      const paid = data.filter((b) => (b.status || '').toUpperCase() === 'PAID');
      setTdsBatches(paid);
      setTotalBatches(paid.length);
      setTotalPages(Math.max(1, Math.ceil(paid.length / pageSize)));
      setCurrentPage(0);
    } catch (err) {
      console.error('fetch tds paid batches error', err);
      setTdsBatches([]);
      setTotalBatches(0);
      setTotalPages(0);
      setCurrentPage(0);
    } finally {
      setLoadingBatches(false);
    }
  }, [pageSize]);

  useEffect(() => {
    fetchTdsBatches();
  }, [fetchTdsBatches]);

  const formatCurrency = (amount) => {
    if (amount == null || Number.isNaN(Number(amount))) return '-';
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(Number(amount));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return 'Invalid Date';
    return d.toLocaleDateString('en-IN');
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return 'Invalid Date';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const datePart = `${day}/${month}/${year}`;
    const timePart = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
    return `${datePart} ${timePart}`;
  };

  const openViewChallanModal = async (batch) => {
    if (!batch || !batch.id) return;
    setSelectedBatchForChallan(batch);
    setViewChallanOpen(true);
    setChallanDetails(null);
    setChallanError('');
    setLoadingChallan(true);

    try {
      const resp = await api.get(`/tds/batches/${batch.id}/challan`);
      setChallanDetails(resp.data || null);
    } catch (err) {
      console.error('Error loading challan details', err);
      if (err.response && err.response.status === 404) {
        setChallanDetails(null);
        setChallanError('');
      } else {
        const msg = err?.response?.data?.message || 'Failed to load challan details.';
        setChallanError(msg);
      }
    } finally {
      setLoadingChallan(false);
    }
  };

  const closeViewChallanModal = () => {
    setViewChallanOpen(false);
    setSelectedBatchForChallan(null);
    setChallanDetails(null);
    setChallanError('');
  };

  const openViewInvoicesModal = async (batch) => {
    setSelectedBatchForInvoices(batch);
    setViewInvoicesOpen(true);
    setBatchInvoices([]);
    setBatchInvoicesError('');

    if (!batch || batch.id == null) {
      setBatchInvoicesError('Batch id is missing; unable to load invoices.');
      return;
    }

    setLoadingBatchInvoices(true);
    try {
      const resp = await api.get(`/invoices/by-tds-batch/${batch.id}`);
      const data = Array.isArray(resp.data) ? resp.data : [];
      setBatchInvoices(data);
      if (!data.length) setBatchInvoicesError('No invoices found for this batch.');
    } catch (err) {
      console.error('Error loading invoices for batch', err);
      const msg = err?.response?.data?.message || 'Failed to load invoice details for this batch.';
      setBatchInvoicesError(msg);
    } finally {
      setLoadingBatchInvoices(false);
    }
  };

  const closeViewInvoicesModal = () => {
    setViewInvoicesOpen(false);
    setSelectedBatchForInvoices(null);
    setBatchInvoices([]);
    setBatchInvoicesError('');
  };

  const fetchVendorsForBatchInvoices = useCallback(async (invoices) => {
    try {
      const vendorIds = [...new Set(invoices.map((inv) => inv.vendorId).filter((id) => id != null && !vendorMap[id]))];
      if (!vendorIds.length) return;
      const responses = await Promise.all(
        vendorIds.map((id) =>
          api
            .get(`/api/vendors/${id}`)
            .then((res) => ({ id, name: res.data?.name || `Vendor ${id}` }))
            .catch(() => ({ id, name: `Vendor ${id}` })),
        ),
      );
      const newMap = {};
      responses.forEach(({ id, name }) => {
        newMap[id] = name;
      });
      if (Object.keys(newMap).length) setVendorMap((prev) => ({ ...prev, ...newMap }));
    } catch (err) {
      console.error('Error fetching vendors for batch invoices', err);
    }
  }, [vendorMap]);

  useEffect(() => {
    if (batchInvoices.length > 0) fetchVendorsForBatchInvoices(batchInvoices);
  }, [batchInvoices, fetchVendorsForBatchInvoices]);

  const invoiceTableHeaders = ['Invoice', 'Invoice Date', 'Vendor Name', 'Total Amount', 'Invoice Payable', 'TDS Amount', 'Filing Qtr', 'Status'];

  const invoiceTableData = batchInvoices.map((inv) => {
    const vendorName = inv.vendorName || vendorMap[inv.vendorId] || (inv.vendor && inv.vendor.name) || 'N/A';
    return [
      inv.invoiceNumber || inv.id || 'N/A',
      formatDate(inv.invoiceDate),
      vendorName,
      formatCurrency(inv.totalAmount),
      formatCurrency(inv.invoicePayable),
      formatCurrency(inv.tdsAmount),
      inv.filingQuarter || '-',
      (inv.status || 'N/A').toString(),
    ];
  });

  const stats = {
    existingBatches: totalBatches || tdsBatches.length,
    totalTdsInBatches: (tdsBatches || []).reduce(
      (s, b) => s + Number(b.totalTdsAmount || b.totalTds || 0),
      0,
    ),
    totalInterestInBatches: (tdsBatches || []).reduce(
      (s, b) => s + Number(b.totalInterest || 0),
      0,
    ),
    totalFinalAmountInBatches: (tdsBatches || []).reduce(
      (s, b) => s + Number(b.finalTdsAmount || 0),
      0,
    ),
  };

  const availableYears = React.useMemo(() => {
    const years = new Set();
    (tdsBatches || []).forEach((b) => {
      if (!b.batchPaidDate) return;
      const d = new Date(b.batchPaidDate);
      if (!Number.isNaN(d.getTime())) {
        years.add(d.getFullYear());
      }
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [tdsBatches]);

  const filteredBatches = React.useMemo(() => {
    const getQuarter = (monthIndex) => {
      const m = monthIndex + 1; // 1-12
      if (m >= 1 && m <= 3) return 'Q1';
      if (m >= 4 && m <= 6) return 'Q2';
      if (m >= 7 && m <= 9) return 'Q3';
      return 'Q4';
    };

    return (tdsBatches || []).filter((b) => {
      if (!b.batchPaidDate) return false;
      const d = new Date(b.batchPaidDate);
      if (Number.isNaN(d.getTime())) return false;

      const year = d.getFullYear();
      const quarter = getQuarter(d.getMonth());
      const y = String(year).padStart(4, '0');
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const ymd = `${y}-${m}-${day}`;

      if (yearFilter !== 'ALL' && String(year) !== String(yearFilter)) return false;
      if (quarterFilter !== 'ALL' && quarter !== quarterFilter) return false;
      if (dateFilter && ymd !== dateFilter) return false;

      return true;
    });
  }, [tdsBatches, yearFilter, quarterFilter, dateFilter]);

  const totalFiltered = filteredBatches.length;

  React.useEffect(() => {
    // reset to first page when filters change
    setCurrentPage(0);
  }, [yearFilter, quarterFilter, dateFilter]);

  const handlePageChange = (page) => {
    const newPage = Number(page) >= 1 ? Number(page) - 1 : 0;
    setCurrentPage(newPage);
  };

  const paginatedBatches = React.useMemo(() => {
    const start = currentPage * pageSize;
    const end = start + pageSize;
    return filteredBatches.slice(start, end);
  }, [filteredBatches, currentPage, pageSize]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 bg-gray-50 min-h-screen">
      <PageHeader
        title="Paid TDS Batches"
        subtitle="View TDS batches that have been marked as paid."
        actions={[
          <Button key="refresh" variant="outline" onClick={fetchTdsBatches}>
            <I.RefreshCw className={`w-4 h-4 mr-2 ${loadingBatches ? 'animate-spin' : ''}`} />
            Refresh
          </Button>,
        ]}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <StatsCard title="Paid Batches" value={`${stats.existingBatches}`} icon={I.Package} />
        <StatsCard
          title="Total TDS in Paid Batches"
          value={formatCurrency(stats.totalTdsInBatches)}
          icon={I.IndianRupee}
        />
        <StatsCard
          title="Total Interest in Paid Batches"
          value={formatCurrency(stats.totalInterestInBatches)}
          icon={I.TrendingUp}
        />
      </div>

      <Card>
        <div className="flex flex-col gap-3 mb-3">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">Paid TDS Batches</h3>
            <div className="inline-flex rounded-md shadow-sm border border-gray-200 bg-white overflow-hidden">
              <button
                type="button"
                className="px-3 py-1 text-xs font-medium bg-white text-gray-700 hover:bg-gray-50 focus:outline-none"
                onClick={() => navigate && navigate('tds-batches')}
              >
                Pending
              </button>
              <button
                type="button"
                className="px-3 py-1 text-xs font-medium bg-blue-600 text-white focus:outline-none"
                onClick={() => navigate && navigate('tds-batches-paid')}
              >
                Paid
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <select
              className="border border-gray-300 rounded px-2 py-1 text-xs sm:text-sm bg-white"
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
            >
              <option value="ALL">All Years</option>
              {availableYears.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>

            <select
              className="border border-gray-300 rounded px-2 py-1 text-xs sm:text-sm bg-white"
              value={quarterFilter}
              onChange={(e) => setQuarterFilter(e.target.value)}
            >
              <option value="ALL">All Quarters</option>
              <option value="Q1">Q1 (Jan-Mar)</option>
              <option value="Q2">Q2 (Apr-Jun)</option>
              <option value="Q3">Q3 (Jul-Sep)</option>
              <option value="Q4">Q4 (Oct-Dec)</option>
            </select>

            <input
              type="date"
              className="border border-gray-300 rounded px-2 py-1 text-xs sm:text-sm bg-white"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            />

            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setYearFilter('ALL');
                setQuarterFilter('ALL');
                setDateFilter('');
              }}
            >
              Clear Filters
            </Button>
          </div>
        </div>

        {loadingBatches ? (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner size="md" />
            <span className="ml-3">Loading paid batches...</span>
          </div>
        ) : totalFiltered === 0 ? (
          <div className="text-sm text-gray-500">No paid TDS batches found for selected filters.</div>
        ) : (
          <div className="overflow-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Batch #
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    TDS Rule
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    Total TDS
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    Interest
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    Final TDS
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Invoices
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Payment Info
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedBatches.map((b, idx) => (
                  <tr key={b.id || b.batchNumber || idx}>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {b.batchNumber || b.id || `#${idx + 1}`}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {b.tdsRule || b.tdsRuleName || '-'}
                    </td>
                    <td className="px-4 py-2 text-sm text-right text-gray-700">
                      {formatCurrency(b.totalTdsAmount || b.totalTds || 0)}
                    </td>
                    <td className="px-4 py-2 text-sm text-right text-gray-700">
                      {formatCurrency(b.totalInterest || 0)}
                    </td>
                    <td className="px-4 py-2 text-sm text-right text-gray-700">
                      {formatCurrency(b.finalTdsAmount || 0)}
                    </td>
                    <td className="px-4 py-2 text-sm">
                      <button
                        type="button"
                        onClick={() => {
                          if (setSelectedBatch) {
                            setSelectedBatch(b);
                          }
                          if (navigate) {
                            navigate('tds-paid-batch-invoices');
                          }
                        }}
                        className="inline-flex items-center justify-center rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 focus:outline-none"
                      >
                        <I.FileText className="w-3 h-3 mr-1" />
                        {Array.isArray(b.invoiceIds)
                          ? `${b.invoiceIds.length} invoice(s)`
                          : b.invoiceCount != null
                            ? `${b.invoiceCount} invoice(s)`
                            : 'View'}
                      </button>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      <div className="flex flex-col text-xs text-gray-700 gap-1">
                        <span>
                          Paid on:{' '}
                          <span className="font-semibold">
                            {b.batchPaidDate ? formatDateTime(b.batchPaidDate) : 'N/A'}
                          </span>
                        </span>
                        <button
                          type="button"
                          onClick={() => openViewChallanModal(b)}
                          className="mt-1 inline-flex items-center justify-center rounded-md border border-blue-200 bg-white px-2 py-0.5 text-[11px] font-medium text-blue-700 shadow-sm hover:bg-blue-50 focus:outline-none"
                        >
                          View Challan
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {totalPages > 1 && !loadingBatches && tdsBatches.length > 0 && (
        <div className="px-4 py-3 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-2">
          <div className="text-xs sm:text-sm text-gray-500">
            Showing
            {' '}
            <span className="font-medium">{Math.min((currentPage + 1) * pageSize, totalBatches)}</span>
            {' '}of{' '}
            <span className="font-medium">{totalBatches}</span>
            {' '}paid batches
          </div>
          <Pagination
            currentPage={currentPage + 1}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}

      {viewChallanOpen && selectedBatchForChallan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">Challan Details</h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleViewChallanDocument}
                  className="text-blue-600 hover:text-blue-800 p-1"
                  title="View Document"
                >
                  <I.Eye className="w-5 h-5" />
                </button>
                <button
                  className="text-gray-500 hover:text-gray-700 p-1"
                  onClick={closeViewChallanModal}
                >
                  <I.X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-4 text-sm max-h-[60vh] overflow-auto">
              <div className="mb-3 text-gray-600">
                <div>
                  Batch #:{' '}
                  <span className="font-medium">
                    {selectedBatchForChallan.batchNumber || selectedBatchForChallan.id}
                  </span>
                </div>
              </div>

              {loadingChallan ? (
                <div className="flex items-center justify-center py-6">
                  <LoadingSpinner size="sm" />
                  <span className="ml-2 text-gray-600">Loading challan details...</span>
                </div>
              ) : challanError ? (
                <div className="text-red-600">{challanError}</div>
              ) : !challanDetails ? (
                <div className="text-gray-500">No TDS for the vendor</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <div className="text-gray-500">BSR Code</div>
                    <div className="font-medium">{challanDetails.bsrCode || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Date of Deposit</div>
                    <div className="font-medium">
                      {challanDetails.dateOfDeposit
                        ? formatDate(challanDetails.dateOfDeposit)
                        : 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">Challan No.</div>
                    <div className="font-medium">
                      {challanDetails.challanSerialNumber || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">TAN</div>
                    <div className="font-medium">{challanDetails.tan || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Assessment Year</div>
                    <div className="font-medium">
                      {challanDetails.assessmentYear || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">Major Head</div>
                    <div className="font-medium">{challanDetails.majorHead || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Minor Head</div>
                    <div className="font-medium">{challanDetails.minorHead || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Nature of Payment</div>
                    <div className="font-medium">
                      {challanDetails.natureOfPayment || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">Total Tax Amount</div>
                    <div className="font-medium">
                      {formatCurrency(challanDetails.totalTaxAmount)}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">CIN</div>
                    <div className="font-medium">{challanDetails.cin || 'N/A'}</div>
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 border-t flex justify-end gap-3">
              <Button variant="secondary" onClick={closeViewChallanModal}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {viewInvoicesOpen && selectedBatchForInvoices && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl">
            <div className="p-4 border-b flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Invoices in Batch</h3>
                <p className="text-sm text-gray-500">
                  Batch:{' '}
                  <span className="font-medium">
                    {selectedBatchForInvoices.batchNumber || selectedBatchForInvoices.id}
                  </span>
                </p>
              </div>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={closeViewInvoicesModal}
              >

                7
              </button>
            </div>

            <div className="p-4 max-h-[70vh] overflow-auto text-sm">
              {loadingBatchInvoices ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner size="md" />
                  <span className="ml-3 text-gray-600">Loading invoices...</span>
                </div>
              ) : batchInvoicesError ? (
                <div className="text-red-600">{batchInvoicesError}</div>
              ) : batchInvoices.length === 0 ? (
                <div className="text-gray-500">No invoices found for this batch.</div>
              ) : (
                <div className="overflow-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {invoiceTableHeaders.map((h) => (
                          <th
                            key={h}
                            className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {invoiceTableData.map((row, idx) => (
                        <tr key={idx}>
                          {row.map((cell, cIdx) => (
                            <td key={cIdx} className="px-4 py-2 text-sm text-gray-700">
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="p-4 border-t flex justify-end gap-3">
              <Button variant="secondary" onClick={closeViewInvoicesModal}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {showPdf && pdfUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-white rounded-lg shadow-xl w-[80vw] h-[85vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 border-b">
              <span className="text-sm font-medium text-gray-700">Challan Document</span>
              <button
                type="button"
                onClick={() => setShowPdf(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <I.X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1">
              {pdfUrl.toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i) ? (
                // Display image in iframe
                <iframe
                  src={pdfUrl}
                  title="Challan Document"
                  className="w-full h-full border-0"
                  frameBorder="0"
                  onLoad={() => console.log('[TDS-PAID-BATCHES] Challan image iframe loaded')}
                  onError={(e) => console.error('[TDS-PAID-BATCHES] Challan image iframe error:', e)}
                />
              ) : (
                // Display PDF (no viewer parameters needed for this implementation)
                <iframe
                  src={pdfUrl}
                  title="Challan Document"
                  className="w-full h-full border-0"
                  onLoad={() => console.log('[TDS-PAID-BATCHES] Challan PDF iframe loaded')}
                  onError={(e) => console.error('[TDS-PAID-BATCHES] Challan PDF iframe error:', e)}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TdsPaidBatchesPage;
